/**
 * @file LandingPage.js
 * @description Public landing page for the application.
 * Displays a welcome message, features, pricing, and options to login or register.
 * Redirects authenticated users to the dashboard.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Button, Card, Navbar, Nav, Badge, Spinner } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import LanguageSelector from '../components/LanguageSelector';
import LandingNavbar from '../components/LandingNavbar';
import { getPlansPublic, getFeatureDefinitions, submitContactMessage } from '../services/api';
import { getPlanFeaturesList, formatPrice } from '../utils/planUtils';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Swal from 'sweetalert2';
import '../phone-input-custom.css'; // Ensure this exists or use inline styles if needed

const ContactForm = ({ t }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePhoneChange = (value) => {
        setFormData({ ...formData, phone: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await submitContactMessage(formData);
            Swal.fire({
                icon: 'success',
                title: t('landingPage.contact.successTitle', '¡Mensaje Enviado!'),
                text: t('landingPage.contact.successMessage', 'Nos pondremos en contacto contigo pronto.'),
                confirmButtonColor: 'var(--ev-action-primary-bg)'
            });
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: t('landingPage.contact.errorTitle', 'Error'),
                text: error.response?.data?.message || t('landingPage.contact.errorMessage', 'Hubo un problema al enviar tu mensaje. Intenta nuevamente.'),
                confirmButtonColor: 'var(--ev-action-primary-bg)'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-3">
                <label htmlFor="name" className="form-label">{t('landingPage.contact.name', 'Nombre')}</label>
                <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="mb-3">
                <label htmlFor="email" className="form-label">{t('landingPage.contact.email', 'Correo Electrónico')}</label>
                <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="mb-3">
                <label htmlFor="phone" className="form-label">{t('landingPage.contact.phone', 'WhatsApp / Teléfono')}</label>
                <PhoneInput
                    international
                    defaultCountry="CO"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className="form-control d-flex"
                    style={{ border: 'none', padding: 0 }} /* Overriding default styles to blend with form-control wrapper if needed, or just use as is */
                />
            </div>
            <div className="mb-3">
                <label htmlFor="subject" className="form-label">{t('landingPage.contact.subject', 'Asunto')}</label>
                <input
                    type="text"
                    className="form-control"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                />
            </div>
            <div className="mb-3">
                <label htmlFor="message" className="form-label">{t('landingPage.contact.message', 'Mensaje')}</label>
                <textarea
                    className="form-control"
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                ></textarea>
            </div>
            <div className="d-grid">
                <Button variant="primary" type="submit" disabled={loading} className="rounded-pill">
                    {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : t('landingPage.contact.submit', 'Enviar Mensaje')}
                </Button>
            </div>
        </form>
    );
};


const LandingPage = () => {
    const { user } = useContext(AuthContext);
    const { t, i18n } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [featureDefinitions, setFeatureDefinitions] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'pse'

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const [plansResponse, featuresResponse] = await Promise.all([
                    getPlansPublic(),
                    getFeatureDefinitions()
                ]);
                setPlans(plansResponse.data);
                setFeatureDefinitions(featuresResponse.data);
            } catch (error) {
                console.error('Failed to fetch plans:', error);
            } finally {
                setLoadingPlans(false);
            }
        };

        fetchPlans();
    }, []);



    // If user is already logged in, redirect to dashboard
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="landing-page" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Navbar */}
            <LandingNavbar />

            {/* Hero Section */}
            <section className="py-5 bg-white">
                <Container className="py-5">
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-5 mb-lg-0">
                            <h1 className="display-4 fw-bold mb-3 text-dark">{t('landingPage.hero.title')}</h1>
                            <p className="lead text-muted mb-4">
                                {t('landingPage.hero.subtitle')}
                            </p>
                            <div className="d-grid gap-2 d-md-block">
                                <Link to="/register">
                                    <Button variant="primary" size="lg" className="me-md-3 px-4 rounded-pill shadow-sm">{t('landingPage.hero.getStarted')}</Button>
                                </Link>
                                <Link to="/login">
                                    <Button variant="outline-secondary" size="lg" className="px-4 rounded-pill">{t('landingPage.hero.signIn')}</Button>
                                </Link>
                            </div>
                        </Col>
                        <Col lg={6} className="text-center">
                            <div className="p-3">
                                <img src="/logo_evazard_transparente.svg" alt="Evazard Dashboard" className="img-fluid" style={{ maxHeight: '400px' }} />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Features Section */}
            <section id="features" className="py-5 bg-light">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">{t('landingPage.features.title')}</h2>
                    </div>
                    <Row>
                        <Col md={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm text-center p-4">
                                <Card.Body>
                                    <div className="mb-3 text-primary">
                                        <i className="bi bi-graph-up fs-1"></i> {/* Bootstrap Icons class, ensure icons are loaded or replace */}
                                        <h3>📊</h3>
                                    </div>
                                    <Card.Title className="fw-bold">{t('landingPage.features.sales.title')}</Card.Title>
                                    <Card.Text className="text-muted">
                                        {t('landingPage.features.sales.description')}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm text-center p-4">
                                <Card.Body>
                                    <div className="mb-3 text-primary">
                                        <h3>👥</h3>
                                    </div>
                                    <Card.Title className="fw-bold">{t('landingPage.features.clients.title')}</Card.Title>
                                    <Card.Text className="text-muted">
                                        {t('landingPage.features.clients.description')}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm text-center p-4">
                                <Card.Body>
                                    <div className="mb-3 text-primary">
                                        <h3>📦</h3>
                                    </div>
                                    <Card.Title className="fw-bold">{t('landingPage.features.inventory.title')}</Card.Title>
                                    <Card.Text className="text-muted">
                                        {t('landingPage.features.inventory.description')}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-5 bg-white">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">{t('landingPage.pricing.title')}</h2>
                        <p className="text-muted">{t('landingPage.pricing.subtitle')}</p>

                        {/* Payment Method Toggle */}
                        <div className="d-flex justify-content-center mb-4 mt-4">
                            <div className="bg-light p-1 rounded-pill d-inline-flex border">
                                <Button
                                    variant={paymentMethod === 'card' ? 'primary' : 'light'}
                                    className="rounded-pill px-4"
                                    onClick={() => setPaymentMethod('card')}
                                >
                                    <i className="bi bi-credit-card-fill me-2"></i>
                                    Suscripción (Tarjeta)
                                </Button>
                                <Button
                                    variant={paymentMethod === 'pse' ? 'primary' : 'light'}
                                    className="rounded-pill px-4"
                                    onClick={() => setPaymentMethod('pse')}
                                >
                                    <span className="me-2 fw-bold">PSE / Nequi</span>
                                    Pago Manual
                                </Button>
                            </div>
                        </div>

                        {paymentMethod === 'card' ? (
                            <Badge bg="info" className="p-2 fs-6 fw-normal text-dark">
                                <i className="bi bi-star-fill me-2 text-warning"></i>
                                {t('landingPage.pricing.trialNote')}
                            </Badge>
                        ) : (
                            <Badge bg="secondary" className="p-2 fs-6 fw-normal">
                                <i className="bi bi-info-circle-fill me-2"></i>
                                El mes de prueba solo aplica para suscripciones con tarjeta.
                            </Badge>
                        )}
                    </div>

                    {loadingPlans ? (
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Row className="justify-content-center">
                            {plans.map((plan) => {
                                const isGrowth = plan.name === 'growth';
                                const planNameKey = plan.name === 'business_network' ? 'business_network' : plan.name; // Adjust key matching if needed

                                return (
                                    <Col key={plan.id} lg={4} md={6} className="mb-4">
                                        <Card className={`h-100 ${isGrowth ? 'border-primary shadow-lg transform-scale' : 'border-0 shadow hover-shadow'}`}>
                                            {isGrowth && (
                                                <div className="position-absolute top-0 start-50 translate-middle">
                                                    <Badge bg="primary" pill className="px-3 py-2 shadow-sm">{t('landingPage.pricing.mostPopular')}</Badge>
                                                </div>
                                            )}
                                            <Card.Header className={`${isGrowth ? 'bg-primary text-white' : 'bg-white'} border-0 pt-4 text-center ${isGrowth ? 'rounded-top' : ''}`}>
                                                <h4 className={`fw-bold ${isGrowth ? 'text-white' : 'text-primary'}`}>
                                                    {t(`planManagement.planNames.${planNameKey}`) || plan.name}
                                                </h4>
                                            </Card.Header>
                                            <Card.Body className={`text-center d-flex flex-column ${isGrowth ? 'bg-light' : ''}`}>
                                                <h1 className="card-title pricing-card-title mt-3">
                                                    {formatPrice(plan.price, plan.currency)}
                                                    <small className="text-muted fw-light fs-4">/mes</small>
                                                </h1>
                                                <ul className="list-unstyled mt-3 mb-4 text-start mx-auto">
                                                    {getPlanFeaturesList(plan, t, featureDefinitions, i18n.language).map((feature, index) => (
                                                        <li key={index} className="mb-2">✓ {feature}</li>
                                                    ))}
                                                </ul>
                                                <div className="mt-auto">
                                                    <Link to={`/register?plan=${plan.name}`}>
                                                        <Button variant={isGrowth ? 'primary' : 'outline-primary'} size="lg" className="w-100 rounded-pill shadow">
                                                            {paymentMethod === 'pse'
                                                                ? t('planManagement.selectPlan', 'Seleccionar Plan')
                                                                : (isGrowth ? t('landingPage.pricing.growth.cta') : t('landingPage.pricing.entrepreneur.cta'))
                                                            }
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}

                    <div className="text-center mt-5">
                        <Link to="/plans-comparison">
                            <Button variant="outline-primary" size="lg" className="rounded-pill px-5">
                                {t('landingPage.pricing.viewFullComparison', 'Ver comparación completa')}
                            </Button>
                        </Link>
                    </div>
                </Container>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-5 bg-light">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">{t('landingPage.contact.title', 'Contáctanos')}</h2>
                        <p className="text-muted">{t('landingPage.contact.subtitle', 'Estamos aquí para resolver tus dudas')}</p>
                    </div>
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            <Card className="border-0 shadow-sm p-4">
                                <Card.Body>
                                    <ContactForm t={t} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>


            {/* Footer */}
            <footer className="bg-dark text-light py-4 mt-auto">
                <Container>
                    <Row>
                        <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                            <small>{t('landingPage.footer.copyright', { year: new Date().getFullYear() })}</small>
                        </Col>
                        <Col md={6} className="text-center text-md-end">
                            <Link to="/privacy-policy" className="text-light text-decoration-none me-3"><small>{t('landingPage.footer.privacy')}</small></Link>
                            <Link to="/terms-of-service" className="text-light text-decoration-none"><small>{t('landingPage.footer.terms')}</small></Link>
                        </Col>
                    </Row>
                </Container>
            </footer>
        </div>
    );
};

export default LandingPage;